import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { SEOHead } from '../seo';
import { seoConfigs } from '../../utils/seo-configs';

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Check for success message from signup
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Pre-fill email if provided
      if (location.state.email) {
        setFormData(prev => ({ ...prev, email: location.state.email }));
      }
      // Clear the state so message doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Basic validation
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password.');
      setIsSubmitting(false);
      return;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors && Array.isArray(data.errors)) {
          const errorMessages = data.errors.map(err => err.msg).join(', ');
          throw new Error(errorMessages);
        }
        throw new Error(data.message || 'Login failed');
      }

      localStorage.setItem('therapylist_token', data.token);
      localStorage.setItem('therapylist_user', JSON.stringify(data.user));

      // Redirect based on user role
      if (data.user.role === 'superadmin' || data.user.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/therapist/dashboard');
      }

    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead {...seoConfigs.login} />
      
      <div className="bg-[#e4fde1] text-[#254e70] min-h-screen flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">


        {/* Login Form */}
        <div className="bg-white rounded-3xl p-8 shadow-xl border border-[#254e70]/10">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-[#254e70] mb-2">
              Welcome Back
            </h1>
            <p className="text-[#254e70]/70">
              Sign in to your TherapyList account
            </p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-green-700 text-sm font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div className="flex flex-col gap-2">
              <label htmlFor="email" className="font-semibold text-[#254e70] text-sm">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="px-4 py-3.5 border-2 border-gray-200 rounded-lg text-[#254e70] focus:outline-none focus:border-[#9dacff] transition-colors duration-200"
                required
                disabled={isSubmitting}
                placeholder="Enter your email"
              />
            </div>

            {/* Password */}
            <div className="flex flex-col gap-2">
              <label htmlFor="password" className="font-semibold text-[#254e70] text-sm">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="px-4 py-3.5 border-2 border-gray-200 rounded-lg text-[#254e70] focus:outline-none focus:border-[#9dacff] transition-colors duration-200"
                required
                disabled={isSubmitting}
                placeholder="Enter your password"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#254e70] text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-[#1d3e5a] hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Signing In...
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Links */}
          <div className="mt-6 text-center space-y-3">
            <p className="text-sm text-[#254e70]/70">
              Don't have an account?{' '}
              <Link to="/join" className="text-[#9dacff] hover:underline font-medium">
                Sign up as a therapist
              </Link>
            </p>
            <p className="text-sm">
              <Link to="/forgot-password" className="text-[#9dacff] hover:underline">
                Forgot your password?
              </Link>
            </p>
          </div>

          {/* Account Approval Info - Only show after signup */}
          {successMessage && (
            <div className="mt-6 p-4 bg-[#e4fde1]/40 rounded-lg border border-[#9dacff]/20">
              <div className="text-center">
                <h4 className="text-sm font-semibold text-[#254e70] mb-1">
                  What's Next?
                </h4>
                <p className="text-xs text-[#254e70]/70 leading-relaxed">
                  Start updating your profile immediately! We'll verify your license and let you know when your profile goes live.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <Link 
            to="/" 
            className="text-[#254e70]/70 hover:text-[#254e70] text-sm transition-colors duration-200"
          >
            ← Back to TherapyList
          </Link>
        </div>
      </div>
    </div>
    </>
  );
}

export default LoginPage; 